import type { ChangeEvent, FocusEvent, FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedNumber } from 'react-intl';

import {
    Button,
    ButtonSize,
    ButtonColorVariant,
    Rounding,
} from '../../../shared/components/button/button';
import { Field } from '../../../shared/components/field/field';
import type { IErrorProps } from '../../../shared/components/form-builder/form-builder';
import { Input } from '../../../shared/components/input/input';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';

import styles from './amount-selector.module.scss';

const style = createStyleHelper(styles, 'amount-selector');

export interface IAmountSelectorProps extends IErrorProps {
    amounts: number[];
    currency: string;
    value: number;
    onChange: (value: number) => void;
    onBlur: (event: FocusEvent) => void;
    className?: string;
}

export const AmountSelector: FunctionComponent<IAmountSelectorProps> = ({
    currency,
    hasError,
    hasSuccess,
    message,
    amounts,
    onChange,
    onBlur,
    value,
    className,
}) => {
    const isDesktop = useDesktop();

    const handleInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            onChange(+event.target.value);
        },
        [onChange],
    );

    return (
        <div className={createClassName([style(), className])}>
            <Field hasError={hasError} message={message} hasSuccess={hasSuccess}>
                <Input
                    name="amount"
                    placeholder={'$0.00'}
                    type="number"
                    onChange={handleInputChange}
                    onBlur={onBlur}
                    value={value ? value.toString() : ''}
                />
            </Field>
            <Field>
                <div className={style('amounts')}>
                    {amounts.map((amount, index) => (
                        <Button
                            className={style('amount')}
                            key={index}
                            type="button"
                            size={isDesktop ? ButtonSize.MEDIUM : ButtonSize.SMALL}
                            buttonColorVariant={
                                value === amount
                                    ? ButtonColorVariant.SOLID
                                    : ButtonColorVariant.SOLID_DISABLED
                            }
                            rounding={Rounding.SMALL}
                            onClick={() => onChange(amount)}
                        >
                            +<FormattedNumber style="currency" value={amount} currency={currency} />
                        </Button>
                    ))}
                </div>
            </Field>
        </div>
    );
};
